// The Timeline model (progress-views design §6) — pure, no I/O.
//
// One point per week (Monday). For each skill: how many of its 4 steps were
// started and how many were done with no game help, each week. A summary
// stacks ONLY the steps at No game help / Steady / In new places, so the line
// rises with growth, not because new skills open up as a child gets older.
//
// Stages never go down (the stage at a week is the highest stage reached by
// then), weeks without play are simply flat, and nothing marks a gap. The
// centre lens draws a review marker every 13 weeks from the child's earliest
// synced date.

import type { IsoDate, SkillStepState, StepStage } from "../domain/types";
import { STEP_COUNT } from "./skillRegistry.generated";
import { addDays, addWeeks, localDay, mondayOf } from "./weeks";
import { firstDate, isOwnOrAbove, stageOn } from "./stages";
import { domainRank, localized, skillById, skillRank } from "./registry";

export type TimelineRange = "3m" | "6m" | "1y" | "all";

export const TIMELINE_RANGES: readonly TimelineRange[] = ["3m", "6m", "1y", "all"];

const RANGE_WEEKS: Record<Exclude<TimelineRange, "all">, number> = { "3m": 13, "6m": 26, "1y": 52 };

export const REVIEW_EVERY_WEEKS = 13;

export interface TimelineStep {
  n: number;
  /** Stage at each week of {@link TimelineModel.weeks}; null = not started. */
  stages: (StepStage | null)[];
  lastPractisedOn: IsoDate | null;
}

export interface TimelineSkill {
  skillId: string;
  label: string;
  domain: string;
  /** Steps started (0–4) per week. */
  started: number[];
  /** Steps with no game help — own or above (0–4) per week. */
  own: number[];
  steps: TimelineStep[];
}

export interface TimelineModel {
  range: TimelineRange;
  weeks: IsoDate[];
  /** Grouped by bed (app order), then registry order. */
  skills: TimelineSkill[];
  /** Per week, the number of steps AT each growth stage (stacked in the chart). */
  summary: { own: number[]; steady: number[]; places: number[] };
  /** Review markers (week dates) inside the window. */
  reviews: IsoDate[];
  hasData: boolean;
}

export interface TimelineInput {
  stepStates: readonly SkillStepState[];
  range?: TimelineRange;
  now?: Date;
  locale?: string;
}

/** The last day of a week, so a stage reached that Monday counts for that week. */
const weekEnd = (monday: IsoDate) => addDays(monday, 6);

export function buildTimeline(input: TimelineInput): TimelineModel {
  const range = input.range ?? "3m";
  const locale = input.locale ?? "en";
  const known = input.stepStates.filter((s) => skillById(s.skillId) && firstDate(s) !== null);

  const end = mondayOf(localDay(input.now ?? new Date()));
  const earliest = known.reduce<IsoDate | null>((m, s) => {
    const d = mondayOf(firstDate(s) as IsoDate);
    return m === null || d < m ? d : m;
  }, null);

  let start: IsoDate;
  if (range === "all") {
    const floor = addWeeks(end, -(RANGE_WEEKS["3m"] - 1));
    start = earliest && earliest < floor ? earliest : floor;
  } else {
    start = addWeeks(end, -(RANGE_WEEKS[range] - 1));
  }

  const weeks: IsoDate[] = [];
  for (let w = start; w <= end; w = addWeeks(w, 1)) weeks.push(w);

  // Group the known rows by skill.
  const bySkill = new Map<string, SkillStepState[]>();
  for (const s of known) {
    const list = bySkill.get(s.skillId) ?? [];
    list.push(s);
    bySkill.set(s.skillId, list);
  }

  const skills: TimelineSkill[] = [...bySkill.entries()]
    .map(([skillId, rows]) => {
      const def = skillById(skillId)!;
      const steps: TimelineStep[] = [...rows]
        .sort((a, b) => a.step - b.step)
        .map((r) => ({
          n: r.step,
          stages: weeks.map((w) => stageOn(r, weekEnd(w))),
          lastPractisedOn: r.lastPractisedOn,
        }));
      const started = weeks.map((_, i) =>
        Math.min(STEP_COUNT, steps.filter((st) => st.stages[i] !== null).length),
      );
      const own = weeks.map((_, i) =>
        Math.min(STEP_COUNT, steps.filter((st) => isOwnOrAbove(st.stages[i])).length),
      );
      return { skillId, label: localized(def.label, locale), domain: def.domain, started, own, steps };
    })
    .sort(
      (a, b) => domainRank(a.domain) - domainRank(b.domain) || skillRank(a.skillId) - skillRank(b.skillId),
    );

  const count = (stage: StepStage) =>
    weeks.map((_, i) => skills.reduce((n, sk) => n + sk.steps.filter((st) => st.stages[i] === stage).length, 0));

  const reviews: IsoDate[] = [];
  if (earliest) {
    for (let r = addWeeks(earliest, REVIEW_EVERY_WEEKS); r <= end; r = addWeeks(r, REVIEW_EVERY_WEEKS)) {
      if (r >= start) reviews.push(r);
    }
  }

  return {
    range,
    weeks,
    skills,
    summary: { own: count("own"), steady: count("steady"), places: count("places") },
    reviews,
    hasData: skills.length > 0,
  };
}
